import { useState, useRef, useEffect, FormEvent, ChangeEvent } from 'react';
import { loadConfig } from '@/config/productConfig';

/* ─── 타입 ───────────────────────────────────────────── */
interface CartItem {
  id: string;
  orderType: string;
  color: string;
  quantity: number;
}

interface OrdererForm {
  name: string;
  tel: string;
  email: string;
  postcode: string;
  address: string;
  address_detail: string;
  request: string;
}

interface FormErrors {
  name?: string;
  tel?: string;
  postcode?: string;
  address?: string;
  cart?: string;
}

declare global {
  interface Window {
    daum: {
      Postcode: new (options: {
        oncomplete: (data: { zonecode: string; roadAddress: string; jibunAddress: string }) => void;
      }) => { open: () => void };
    };
  }
}

const INITIAL_FORM: OrdererForm = {
  name: '',
  tel: '',
  email: '',
  postcode: '',
  address: '',
  address_detail: '',
  request: '',
};

const FORM_SUBMIT_URL = 'https://readdy.ai/api/form/d7kceqg9tv6a23o162t0';

const CTASection = () => {
  const [cfg] = useState(() => loadConfig());

  const [selectedType, setSelectedType] = useState('');
  const [selectedColor, setSelectedColor] = useState('');
  const [cart, setCart] = useState<CartItem[]>([]);
  const [form, setForm] = useState<OrdererForm>(INITIAL_FORM);
  const [errors, setErrors] = useState<FormErrors>({});
  const [status, setStatus] = useState<'idle' | 'sending' | 'done' | 'error'>('idle');
  const [errorMsg, setErrorMsg] = useState('');
  const [showModal, setShowModal] = useState(false);

  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) e.target.classList.add('opacity-100', 'translate-y-0');
        });
      },
      { threshold: 0.1 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  /* 색상 선택 → 장바구니 추가 */
  const handleColorSelect = (color: string) => {
    if (!selectedType || !color) return;
    const key = `${selectedType}__${color}`;
    setCart((prev) => {
      const existing = prev.find((item) => item.id === key);
      if (existing) {
        return prev.map((item) =>
          item.id === key ? { ...item, quantity: item.quantity + 1 } : item
        );
      }
      return [...prev, { id: key, orderType: selectedType, color, quantity: 1 }];
    });
    setErrors((prev) => ({ ...prev, cart: undefined }));
    setSelectedType('');
    setSelectedColor('');
  };

  const changeQty = (id: string, delta: number) => {
    setCart((prev) =>
      prev.map((item) =>
        item.id === id ? { ...item, quantity: Math.max(1, item.quantity + delta) } : item
      )
    );
  };

  const setQty = (id: string, val: number) => {
    const qty = Math.max(1, isNaN(val) ? 1 : val);
    setCart((prev) =>
      prev.map((item) => (item.id === id ? { ...item, quantity: qty } : item))
    );
  };

  const removeItem = (id: string) => {
    setCart((prev) => prev.filter((item) => item.id !== id));
  };

  const totalQty = cart.reduce((sum, item) => sum + item.quantity, 0);

  const handleChange = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    if (errors[name as keyof FormErrors]) {
      setErrors((prev) => ({ ...prev, [name]: undefined }));
    }
  };

  const openPostcode = () => {
    if (!window.daum?.Postcode) return;
    new window.daum.Postcode({
      oncomplete: (data) => {
        setForm((prev) => ({
          ...prev,
          postcode: data.zonecode,
          address: data.roadAddress || data.jibunAddress,
        }));
        setErrors((prev) => ({ ...prev, postcode: undefined, address: undefined }));
      },
    }).open();
  };

  const validate = (): boolean => {
    const newErrors: FormErrors = {};
    if (cart.length === 0) newErrors.cart = '주문 옵션을 1개 이상 선택해 주세요.';
    if (!form.name.trim()) newErrors.name = '이름을 입력해 주세요.';
    if (!form.tel.trim()) newErrors.tel = '연락처를 입력해 주세요.';
    if (!form.postcode.trim()) newErrors.postcode = '우편번호를 검색해 주세요.';
    if (!form.address.trim()) newErrors.address = '기본 주소를 입력해 주세요.';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const resetForm = () => {
    setCart([]);
    setForm(INITIAL_FORM);
    setErrors({});
    setStatus('idle');
    setErrorMsg('');
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!validate()) return;
    if (form.request.length > 500) return;

    setStatus('sending');
    setErrorMsg('');

    const orderSummary = cart
      .map((item) => `${item.orderType}/${item.color} x${item.quantity}${cfg.unit}`)
      .join(', ');

    const params = new URLSearchParams();
    params.append('name', form.name);
    params.append('tel', form.tel);
    params.append('email', form.email);
    params.append('zipcode', form.postcode);
    params.append('address', `${form.address} ${form.address_detail}`.trim());
    params.append('order_items', orderSummary);
    params.append('total_qty', `${totalQty}${cfg.unit}`);
    params.append('delivery_fee', `${cfg.deliveryFee.toLocaleString()}원`);
    params.append('request', form.request);

    try {
      const res = await fetch(FORM_SUBMIT_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: params.toString(),
      });

      if (res.ok) {
        setStatus('done');
        setShowModal(true);
        resetForm();
      } else {
        setStatus('error');
        setErrorMsg('주문 처리 중 오류가 발생했습니다. 다시 시도해 주세요.');
      }
    } catch {
      setStatus('error');
      setErrorMsg('네트워크 오류가 발생했습니다. 잠시 후 다시 시도해 주세요.');
    }
  };

  const inputClass =
    'w-full border border-gray-200 rounded-md px-3 py-2.5 text-sm text-[#1B2A4A] placeholder-gray-300 focus:outline-none focus:border-[#FF6B35] transition-colors bg-white';
  const inputErrorClass =
    'w-full border border-red-400 rounded-md px-3 py-2.5 text-sm text-[#1B2A4A] placeholder-gray-300 focus:outline-none focus:border-red-500 transition-colors bg-white';
  const labelClass = 'block text-xs font-semibold text-[#1B2A4A] mb-1.5';
  const errorClass = 'text-red-500 text-xs mt-1';
  const getInputClass = (field: keyof FormErrors) =>
    errors[field] ? inputErrorClass : inputClass;

  return (
    <>
      {/* 주문 완료 모달 */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4">
          <div className="bg-white rounded-2xl p-8 max-w-sm w-full text-center">
            <div className="w-16 h-16 flex items-center justify-center bg-green-100 rounded-full mx-auto mb-4">
              <i className="ri-checkbox-circle-fill text-green-500 text-3xl" />
            </div>
            <h3 className="text-xl font-bold text-[#1B2A4A] mb-2">주문이 접수되었습니다!</h3>
            <p className="text-sm text-gray-500 mb-6 leading-relaxed">
              담당자가 확인 후 연락드리겠습니다.<br />
              문의: <strong className="text-[#FF6B35]">010-8640-3977</strong>
            </p>
            <button
              type="button"
              onClick={() => setShowModal(false)}
              className="w-full bg-[#FF6B35] text-white font-semibold py-3 rounded-md hover:bg-[#E5561F] transition-colors cursor-pointer whitespace-nowrap"
            >
              확인
            </button>
          </div>
        </div>
      )}

      <section id="order" className="py-24 bg-[#F8F9FC]">
        <div className="max-w-5xl mx-auto px-6 lg:px-8">

          {/* 타이틀 */}
          <div className="text-center mb-14">
            <span className="text-[#FF6B35] text-sm font-semibold tracking-widest uppercase">Order</span>
            <h2 className="mt-3 text-3xl lg:text-4xl font-bold text-[#1B2A4A] font-display">
              제품 주문하기
            </h2>
            <p className="mt-4 text-gray-500 text-base max-w-xl mx-auto">
              원하시는 제품을 선택하고 주문 정보를 입력해 주세요
            </p>
            <div className="mt-4 w-12 h-1 bg-[#FF6B35] mx-auto rounded-full" />
          </div>

          <div
            ref={ref}
            className="grid grid-cols-1 lg:grid-cols-5 gap-8 opacity-0 translate-y-8 transition-all duration-700"
          >
            {/* ── 좌측 주문 안내 카드 ── */}
            <div className="lg:col-span-2 flex flex-col gap-5">

              {/* 연락처 정보 */}
              <div className="bg-white rounded-2xl border border-gray-100 p-6 flex flex-col gap-4">
                <h3 className="text-base font-bold text-[#1B2A4A] flex items-center gap-2">
                  <i className="ri-information-fill text-[#FF6B35]" />
                  주문 안내
                </h3>

                {/* 전화 */}
                <div className="flex items-start gap-3">
                  <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-[#FF6B35]/10 flex-shrink-0">
                    <i className="ri-phone-fill text-[#FF6B35] text-sm" />
                  </div>
                  <div>
                    <p className="text-xs text-gray-400 mb-0.5">전화 문의</p>
                    <p className="text-[#1B2A4A] font-semibold text-sm">032-667-9023</p>
                  </div>
                </div>

                {/* 이메일 */}
                <div className="flex items-start gap-3">
                  <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-[#FF6B35]/10 flex-shrink-0">
                    <i className="ri-mail-fill text-[#FF6B35] text-sm" />
                  </div>
                  <div>
                    <p className="text-xs text-gray-400 mb-0.5">이메일</p>
                    <p className="text-[#1B2A4A] font-semibold text-sm">ceo@smartcap.co.kr</p>
                  </div>
                </div>

                {/* 영업시간 */}
                <div className="flex items-start gap-3">
                  <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-[#FF6B35]/10 flex-shrink-0">
                    <i className="ri-time-fill text-[#FF6B35] text-sm" />
                  </div>
                  <div>
                    <p className="text-xs text-gray-400 mb-0.5">영업시간</p>
                    <p className="text-[#1B2A4A] font-semibold text-sm">평일 09:00 ~ 18:00</p>
                  </div>
                </div>

                {/* 배송비 */}
                <div className="flex items-start gap-3">
                  <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-[#FF6B35]/10 flex-shrink-0">
                    <i className="ri-truck-fill text-[#FF6B35] text-sm" />
                  </div>
                  <div>
                    <p className="text-xs text-gray-400 mb-0.5">배송비</p>
                    <p className="text-[#1B2A4A] font-semibold text-sm">
                      {cfg.deliveryFee.toLocaleString()}원 (건당)
                    </p>
                  </div>
                </div>
              </div>

              {/* 제품별 단가 카드 */}
              <div className="bg-white rounded-2xl border border-gray-100 p-6">
                <p className="text-sm font-bold text-[#1B2A4A] mb-4 flex items-center gap-2">
                  <i className="ri-price-tag-3-fill text-[#FF6B35]" />
                  제품별 단가 ({cfg.unit}당)
                </p>
                <div className="flex flex-col gap-3">
                  {cfg.orderTypes.map((type) => (
                    <div
                      key={type}
                      className="flex justify-between items-center py-2.5 px-3 rounded-xl bg-[#F8F9FC] border border-gray-100"
                    >
                      <div className="flex items-center gap-2">
                        <div className="w-2 h-2 rounded-full bg-[#FF6B35]" />
                        <span className="text-sm font-medium text-[#1B2A4A]">{type}</span>
                      </div>
                      <span className="text-sm font-bold text-gray-400 italic">
                        가격 미정
                      </span>
                    </div>
                  ))}
                </div>
                <p className="text-xs text-gray-400 mt-3 leading-relaxed">
                  ※ 단가는 주문 수량에 따라 달라질 수 있습니다.<br />
                  전화 또는 이메일로 문의해 주세요.
                </p>
              </div>

              {/* 특허 카드 */}
              <div className="bg-[#1B2A4A] rounded-2xl p-5 text-white">
                <i className="ri-award-fill text-[#FF6B35] text-2xl mb-2 block" />
                <p className="font-bold text-sm mb-1">특허 등록 완료 제품</p>
                <p className="text-white/60 text-xs leading-relaxed">
                  2026년 7월 30일 (특허 완료)<br />
                  방수 패킹이 구비된 지붕캡
                </p>
              </div>
            </div>

            {/* ── 우측 주문 폼 ── */}
            <div className="lg:col-span-3">
              <form
                id="order-form"
                data-readdy-form
                onSubmit={handleSubmit}
                className="bg-white rounded-2xl p-8 border border-gray-100"
              >

                {/* STEP 1: 옵션 선택 */}
                <div className="mb-6">
                  <div className="flex items-center gap-2 mb-4">
                    <span className="w-6 h-6 flex items-center justify-center rounded-full bg-[#FF6B35] text-white text-xs font-bold flex-shrink-0">1</span>
                    <p className="text-sm font-bold text-[#1B2A4A]">옵션 선택</p>
                  </div>
                  <p className="text-xs text-gray-400 mb-4">
                    주문 유형과 색상을 순서대로 선택하면 아래 목록에 자동으로 추가됩니다.
                  </p>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className={labelClass}>
                        주문 유형 <span className="text-[#FF6B35]">*</span>
                      </label>
                      <select
                        value={selectedType}
                        onChange={(e) => {
                          setSelectedType(e.target.value);
                          setSelectedColor('');
                        }}
                        className={inputClass}
                      >
                        <option value="">주문 유형 선택</option>
                        {cfg.orderTypes.map((t) => (
                          <option key={t} value={t}>{t}</option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className={labelClass}>
                        색상 <span className="text-[#FF6B35]">*</span>
                      </label>
                      <select
                        value={selectedColor}
                        onChange={(e) => {
                          setSelectedColor(e.target.value);
                          handleColorSelect(e.target.value);
                        }}
                        disabled={!selectedType}
                        className={`${inputClass} disabled:bg-gray-50 disabled:text-gray-300 disabled:cursor-not-allowed`}
                      >
                        <option value="">색상 선택</option>
                        {cfg.colors.map((c) => (
                          <option key={c} value={c}>{c}</option>
                        ))}
                      </select>
                      {!selectedType && (
                        <p className="text-gray-400 text-xs mt-1">주문 유형을 먼저 선택해 주세요</p>
                      )}
                    </div>
                  </div>
                </div>

                {/* STEP 2: 선택된 주문 목록 */}
                <div className="mb-6">
                  <div className="flex items-center gap-2 mb-4">
                    <span className="w-6 h-6 flex items-center justify-center rounded-full bg-[#FF6B35] text-white text-xs font-bold flex-shrink-0">2</span>
                    <p className="text-sm font-bold text-[#1B2A4A]">선택된 주문 목록</p>
                    {cart.length > 0 && (
                      <span className="ml-auto text-xs font-semibold text-[#FF6B35] bg-[#FF6B35]/10 px-2 py-0.5 rounded-full">
                        {cart.length}개 선택됨
                      </span>
                    )}
                  </div>

                  {cart.length === 0 ? (
                    <div className={`border-2 border-dashed rounded-xl py-10 flex flex-col items-center justify-center gap-2 ${errors.cart ? 'border-red-300 bg-red-50/30' : 'border-gray-200'}`}>
                      <div className="w-10 h-10 flex items-center justify-center">
                        <i className={`ri-shopping-cart-2-line text-3xl ${errors.cart ? 'text-red-300' : 'text-gray-300'}`} />
                      </div>
                      <p className={`text-sm ${errors.cart ? 'text-red-400' : 'text-gray-400'}`}>
                        {errors.cart ?? '위에서 옵션을 선택하면 여기에 추가됩니다'}
                      </p>
                    </div>
                  ) : (
                    <div className="flex flex-col gap-3">
                      {cart.map((item) => (
                        <div
                          key={item.id}
                          className="border border-gray-100 rounded-xl p-4 bg-gray-50/50"
                        >
                          <div className="flex items-center justify-between mb-3">
                            <div className="flex items-center gap-2">
                              <div className="w-2 h-2 rounded-full bg-[#FF6B35]" />
                              <span className="text-sm font-semibold text-[#1B2A4A]">
                                {item.orderType} · {item.color}
                              </span>
                            </div>
                            <button
                              type="button"
                              onClick={() => removeItem(item.id)}
                              className="w-6 h-6 flex items-center justify-center rounded-full hover:bg-gray-200 transition-colors cursor-pointer text-gray-400 hover:text-gray-600 flex-shrink-0"
                            >
                              <i className="ri-close-line text-sm" />
                            </button>
                          </div>
                          <div className="flex items-center gap-2">
                            <button
                              type="button"
                              onClick={() => changeQty(item.id, -1)}
                              className="w-8 h-8 flex items-center justify-center rounded-md border border-gray-200 bg-white text-[#1B2A4A] hover:bg-gray-100 transition-colors cursor-pointer whitespace-nowrap"
                            >
                              <i className="ri-subtract-line text-xs" />
                            </button>
                            <input
                              type="number"
                              min={1}
                              value={item.quantity}
                              onChange={(e) => setQty(item.id, parseInt(e.target.value))}
                              className="w-14 border border-gray-200 rounded-md px-2 py-1.5 text-sm text-center text-[#1B2A4A] focus:outline-none focus:border-[#FF6B35] bg-white"
                            />
                            <button
                              type="button"
                              onClick={() => changeQty(item.id, 1)}
                              className="w-8 h-8 flex items-center justify-center rounded-md border border-gray-200 bg-white text-[#1B2A4A] hover:bg-gray-100 transition-colors cursor-pointer whitespace-nowrap"
                            >
                              <i className="ri-add-line text-xs" />
                            </button>
                            <span className="text-xs text-gray-400">{cfg.unit}</span>
                            <span className="ml-auto text-xs text-gray-400 font-medium">단가 미정</span>
                          </div>
                        </div>
                      ))}

                      {/* 총 수량 요약 */}
                      <div className="bg-[#FF6B35]/8 border border-[#FF6B35]/20 rounded-xl px-4 py-3 flex items-center justify-between">
                        <span className="text-sm text-gray-600">
                          총 수량: <strong className="text-[#1B2A4A]">{totalQty}{cfg.unit}</strong>
                        </span>
                        <span className="text-xs text-gray-400">배송비: {cfg.deliveryFee.toLocaleString()}원 별도</span>
                      </div>
                    </div>
                  )}
                </div>

                {/* 주문자 정보 — 옵션 선택 후 표시 */}
                {cart.length > 0 && (
                  <>
                    <div className="border-t border-gray-100 mb-6" />

                    <div className="mb-6">
                      <div className="flex items-center gap-2 mb-4">
                        <span className="w-6 h-6 flex items-center justify-center rounded-full bg-[#1B2A4A] text-white text-xs font-bold flex-shrink-0">3</span>
                        <p className="text-sm font-bold text-[#1B2A4A]">주문자 정보 입력</p>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                        <div>
                          <label className={labelClass}>
                            이름 <span className="text-[#FF6B35]">*</span>
                          </label>
                          <input
                            type="text"
                            name="name"
                            placeholder="홍길동"
                            value={form.name}
                            onChange={handleChange}
                            className={getInputClass('name')}
                          />
                          {errors.name && <p className={errorClass}>{errors.name}</p>}
                        </div>
                        <div>
                          <label className={labelClass}>
                            연락처 <span className="text-[#FF6B35]">*</span>
                          </label>
                          <input
                            type="tel"
                            name="tel"
                            placeholder="010-0000-0000"
                            value={form.tel}
                            onChange={handleChange}
                            className={getInputClass('tel')}
                          />
                          {errors.tel && <p className={errorClass}>{errors.tel}</p>}
                        </div>
                      </div>

                      <div className="mb-4">
                        <label className={labelClass}>이메일</label>
                        <input
                          type="email"
                          name="email"
                          placeholder="example@email.com"
                          value={form.email}
                          onChange={handleChange}
                          className={inputClass}
                        />
                      </div>

                      <div className="mb-4">
                        <label className={labelClass}>
                          우편번호 <span className="text-[#FF6B35]">*</span>
                        </label>
                        <div className="flex gap-2">
                          <input
                            type="text"
                            name="postcode"
                            placeholder="우편번호"
                            value={form.postcode}
                            readOnly
                            className={`${getInputClass('postcode')} flex-1 bg-gray-50 cursor-default`}
                          />
                          <button
                            type="button"
                            onClick={openPostcode}
                            className="px-4 py-2.5 bg-[#1B2A4A] text-white text-sm font-medium rounded-md hover:bg-[#243660] transition-colors cursor-pointer whitespace-nowrap"
                          >
                            주소 검색
                          </button>
                        </div>
                        {errors.postcode && <p className={errorClass}>{errors.postcode}</p>}
                      </div>

                      <div className="mb-4">
                        <label className={labelClass}>기본 주소</label>
                        <input
                          type="text"
                          name="address"
                          placeholder="우편번호 검색 후 자동 입력"
                          value={form.address}
                          readOnly
                          className={`${getInputClass('address')} bg-gray-50 cursor-default`}
                        />
                        {errors.address && <p className={errorClass}>{errors.address}</p>}
                      </div>

                      <div className="mb-4">
                        <label className={labelClass}>상세 주소</label>
                        <input
                          type="text"
                          name="address_detail"
                          placeholder="동/호수, 건물명 등"
                          value={form.address_detail}
                          onChange={handleChange}
                          className={inputClass}
                        />
                      </div>

                      <div>
                        <label className={labelClass}>
                          요청 사항
                          <span className={`float-right font-normal ${form.request.length > 450 ? 'text-red-400' : 'text-gray-400'}`}>
                            {form.request.length}/500
                          </span>
                        </label>
                        <textarea
                          name="request"
                          rows={3}
                          maxLength={500}
                          value={form.request}
                          onChange={handleChange}
                          placeholder="납기 일정, 현장 규모, 수량 등 요청사항"
                          className={`${inputClass} resize-none`}
                        />
                      </div>
                    </div>

                    <div className="border-t border-gray-100 mb-6" />

                    {/* 에러 메시지 */}
                    {status === 'error' && (
                      <div className="mb-4 bg-red-50 border border-red-200 rounded-md p-3 flex items-center gap-2">
                        <i className="ri-error-warning-fill text-red-500" />
                        <p className="text-red-700 text-sm">{errorMsg}</p>
                      </div>
                    )}

                    <button
                      type="submit"
                      disabled={status === 'sending'}
                      className="w-full bg-[#FF6B35] text-white font-semibold py-3.5 rounded-md hover:bg-[#E5561F] transition-colors cursor-pointer whitespace-nowrap disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2 text-base"
                    >
                      {status === 'sending' ? (
                        <>
                          <i className="ri-loader-4-line animate-spin" />
                          처리 중...
                        </>
                      ) : (
                        <>
                          <i className="ri-send-plane-fill" />
                          주문 접수하기
                        </>
                      )}
                    </button>

                    <p className="text-center text-gray-400 text-xs mt-3">
                      제출 시 개인정보 수집·이용에 동의하는 것으로 간주됩니다.
                    </p>
                  </>
                )}

                {cart.length === 0 && (
                  <div className="text-center py-4">
                    <p className="text-gray-400 text-sm">
                      주문 옵션을 선택하면 주문자 정보 입력란이 나타납니다.
                    </p>
                  </div>
                )}

              </form>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default CTASection;
